import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import { MomentModule } from 'ngx-moment';

import { GroupedOpenQuestionAnswer } from '@recruitee/candidate-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  AvatarComponent,
  CollapsibleCardComponent,
  DateComponent,
  HeadingComponent,
  OfferStatusIndicatorComponent,
  SegmentedButtonType,
  TruncateComponent,
} from '@recruitee/user-interface';

import { CollapsibleCardAnalyticsDirective } from '../collapsible-card/collapsible-card-analytics.directive';
import { CandidateQuestionsResultComponent } from './candidate-questions-results/candidate-questions-result.component';
import type { CandidateQuestionsModuleTranslations } from './translations';
import { CandidateQuestionsTranslations } from './translations';
import { QuestionsMode } from './types';

@Component({
  selector: 'rt-candidate-questions',
  templateUrl: './candidate-questions.component.html',
  styleUrls: ['./candidate-questions.component.less'],
  host: {
    '[class.is-empty]':
      '!this.isReferralQuestionsExist && !this.isQuestionnairesExist && !this.isGroupedScreeningQuestionsExist',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeadingComponent,
    CandidateQuestionsResultComponent,
    TranslatePipe,
    CollapsibleCardComponent,
    CollapsibleCardAnalyticsDirective,
    TruncateComponent,
    MomentModule,
    AvatarComponent,
    OfferStatusIndicatorComponent,
    DateComponent,
  ],
})
export class CandidateQuestionsComponent {
  public readonly QuestionsMode: typeof QuestionsMode = QuestionsMode;

  @Input() public visibilityTplRef: TemplateRef<any>;
  @Input() public questionnaire?: GroupedOpenQuestionAnswer;
  @Input() public referralQuestions?: GroupedOpenQuestionAnswer;
  @Input() public groupedScreeningQuestions?: GroupedOpenQuestionAnswer;
  @Input() public canManageVisibility: boolean = false;
  @Input() public canManageCandidates: boolean = false;
  @Input() public canViewQuestionnaires: boolean = false;
  @Input() public canViewOpenQuestions: boolean = false;
  @Input() public referralsEnabled: boolean = false;
  @Input({ transform: booleanAttribute }) public shouldShowOfferTitle: boolean = true;

  public segmentedButtons: Array<SegmentedButtonType<QuestionsMode>> = [];

  public title: string | undefined;

  public mode: QuestionsMode;

  public get isGroupedScreeningQuestionsExist(): boolean {
    return (
      !!this.groupedScreeningQuestions &&
      this.groupedScreeningQuestions.openQuestionAnswers.length > 0 &&
      this.canViewOpenQuestions
    );
  }

  public get isReferralQuestionsExist(): boolean {
    return !!this.referralQuestions && this.referralsEnabled;
  }

  public get isQuestionnairesExist(): boolean {
    return !!this.questionnaire && this.canViewQuestionnaires;
  }

  private get defaultMode(): QuestionsMode | undefined {
    if (this.isGroupedScreeningQuestionsExist) {
      return QuestionsMode.GroupedScreeningQuestions;
    } else if (this.isReferralQuestionsExist) {
      return QuestionsMode.ReferralQuestions;
    } else if (this.isQuestionnairesExist) {
      return QuestionsMode.Questionnaires;
    }

    return;
  }

  constructor(
    private i18n: I18nService,
    @Inject(CandidateQuestionsTranslations)
    public translations: CandidateQuestionsModuleTranslations,
  ) {}

  public ngOnInit() {
    this.setSegmentButtons();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.mode = this.mode || this.defaultMode!;
    this.resolveTitle();

    if ('questionnaires' in changes || 'referralsQuestions' in changes) {
      this.setSegmentButtons();
    }
  }

  public setSegmentButtons(): void {
    this.segmentedButtons = [
      {
        isActive: this.mode === QuestionsMode.GroupedScreeningQuestions,
        value: QuestionsMode.GroupedScreeningQuestions,
        label: this.i18n.translate(this.translations.titles.questions),
        isAvailable: this.isGroupedScreeningQuestionsExist,
      },
      {
        isActive: this.mode === QuestionsMode.ReferralQuestions,
        value: QuestionsMode.ReferralQuestions,
        label: this.i18n.translate(<string>this.translations.titles.referralQuestions),
        isAvailable: this.isReferralQuestionsExist,
      },
      {
        isActive: this.mode === QuestionsMode.Questionnaires,
        value: QuestionsMode.Questionnaires,
        label: this.i18n.translate(this.translations.titles.questionnaires),
        isAvailable: this.isQuestionnairesExist,
      },
    ];
  }

  private resolveTitle(): void {
    const i18nKeys: Record<QuestionsMode, string | undefined> = {
      [QuestionsMode.Questionnaires]: this.translations.titles.questionnaires,
      [QuestionsMode.ReferralQuestions]: this.translations.titles.referralQuestions,
      [QuestionsMode.GroupedScreeningQuestions]: this.translations.titles.questions,
    };

    this.title = i18nKeys[this.mode];
  }
}
